import React, {forwardRef, useImperativeHandle, useState, useRef} from 'react';
import {makeStyles} from 'tss-react/mui';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';

import usePtrnEstadoSimple from '../hooks/usePtrnEstadoSimple/usePtrnEstadoSimple';
import {PtrnReportePasosProps} from './PtrnReporte2PasosTypes';
import CampoEditable from '../components/CampoEditable';
import {PtrnAccion, PtrnIcons, PtrnTipoAccion} from '../patronTypes';
import PtrnAccionButton from '../components/PtrnAccionButton';
import {PtrnEstilosGenerales} from '../patronUtils';
import GroupBox from '../../ui/GroupBox';
import {PtrnStepperButton} from '../components/PtrnStepper';
import LeyendaItemList from '../components/LeyendaItemList';
import PtrnTitulo from '../components/PtrnTitulo';
import {PtrnMainContentProps, PtrnStateRef} from '../patronProps';
import PtrnContainerSimple from '../components/PtrnContainerSimple';
import {PtrnStepperRef} from '../components/PtrnStepper/PtrnStepperTypes';

const useStyles = makeStyles<number>()((theme, maxHeightVhMain) => ({
   containerMainContent: PtrnEstilosGenerales.containerMainContent(maxHeightVhMain),
   leyendaLabel: {
      textTransform: 'capitalize',
      fontSize: 'smaller'
   }
}));

const PtrnReportePasos = forwardRef<PtrnStateRef, PtrnReportePasosProps & PtrnMainContentProps>(
   (
      {
         filtrosConfig,
         titulo,
         children,
         maxHeightVhMain,
         mostrarLeyenda,
         paso1,
         paso2,
         leyendaItems,
         leyendaValues,
         leyendaTooltips,
         onClickCancelar,
         onClickRecuperar,
         pasosGridProps,
         cancelarGridProps,
         leyendaGridProps,
         footerGridProps,
         localizacion
      },
      ref
   ) => {
      //#region fns globales
      const {classes} = useStyles(maxHeightVhMain);
      const [state, handleOnChangeState, {handleOnBlur, inicializarEstado}] =
         usePtrnEstadoSimple(filtrosConfig);
      const [pasoActivo, setPasoActivo] = useState(0);
      //#endregion
      //#region acciones footer
      const stepperRef = useRef<PtrnStepperRef>(null);
      function inicializarStepper() {
         if (stepperRef && stepperRef.current) {
            stepperRef.current.inicializarStepper();
         }
      }
      const accionRecuperar: PtrnAccion = {
         label: 'Recuperar',
         icon: PtrnIcons.search,
         tipo: PtrnTipoAccion.neutra,
         fn: (data, auxParams) => {
            inicializarStepper();
            onClickRecuperar(data, auxParams);
         },
         descripcion: 'Recuperar la información del reporte de acuerdo a los filtros especificados'
      };
      const accionCancelar: PtrnAccion = {
         label: 'Cancelar',
         icon: PtrnIcons.cancel,
         tipo: PtrnTipoAccion.neutra,
         fn: (data, auxParams) => {
            inicializarStepper();
            inicializarEstado();
            onClickCancelar(data, auxParams);
         },
         descripcion: 'Reinicia los campos a sus valores por defecto'
      };
      //#endregion

      useImperativeHandle(ref, () => {
         return {
            state,
            handleOnChangeState,
            setPaso: (n: number) => setPasoActivo(n)
         };
      });

      const ptrnRef = null;
      const idCompPrefix = 'ptrn-rep-pasos';
      //#region returns
      return (
         <PtrnContainerSimple id={`${idCompPrefix}-container`} noMargin>
            <Grid
               id={`${idCompPrefix}-grid-root`}
               container
               direction="column"
               justifyContent="space-between"
               spacing={2}
            >
               <Grid
                  id={`${idCompPrefix}-grid-root`}
                  container
                  direction="column"
                  justifyContent="space-between"
                  spacing={2}
               >
                  <Grid
                     id={`${idCompPrefix}-grid-header`}
                     container
                     alignItems="center"
                     spacing={1}
                     item
                     xs={12}
                  >
                     <Grid id={`${idCompPrefix}-grid-header-titulo`} item xs={3}>
                        <PtrnTitulo>{titulo}</PtrnTitulo>
                     </Grid>
                     <Grid
                        id={`${idCompPrefix}-grid-header-filtros`}
                        container
                        item
                        xs={7}
                        justifyContent="flex-end"
                     >
                        {filtrosConfig.map((curr, inx) => {
                           return (
                              <CampoEditable
                                 key={curr.name}
                                 campoConfig={curr}
                                 camposData={state}
                                 value={
                                    state[curr.name] !== undefined ? state[curr.name].valor : state
                                 }
                                 onChange={handleOnChangeState}
                                 onBlur={handleOnBlur}
                                 ptrnRef={ptrnRef}
                              />
                           );
                        })}
                     </Grid>
                     <Grid
                        id={`${idCompPrefix}-grid-header-buscar`}
                        container
                        item
                        xs={2}
                        justifyContent="flex-end"
                     >
                        {onClickRecuperar && (
                           <PtrnAccionButton
                              name={`${idCompPrefix}-btn-recuperar`}
                              accion={accionRecuperar}
                              camposData={state}
                              fullWidth
                           />
                        )}
                     </Grid>
                  </Grid>
                  <Grid item xs={12}>
                     <Divider />
                  </Grid>
                  <Grid
                     id={`${idCompPrefix}-grid-body`}
                     className={classes.containerMainContent}
                     container
                     alignItems="flex-start"
                     spacing={1}
                     item
                     xs={12}
                  >
                     {children}
                  </Grid>
                  <Grid
                     alignContent="center"
                     {...footerGridProps}
                     id={`${idCompPrefix}-grid-footer`}
                     container
                     item
                     xs={12}
                  >
                     <Grid
                        spacing={1}
                        justifyContent="flex-start"
                        alignContent="center"
                        xs={2}
                        {...cancelarGridProps}
                        id={`${idCompPrefix}-grid-footer-cancelar`}
                        container
                        item
                     >
                        {onClickCancelar && (
                           <PtrnAccionButton
                              name={`${idCompPrefix}-btn-cancelar`}
                              accion={accionCancelar}
                              camposData={state}
                              fullWidth
                           />
                        )}
                     </Grid>
                     <Grid
                        paddingLeft={2}
                        paddingRight={2}
                        xs={5}
                        spacing={1}
                        justifyContent="flex-end"
                        alignContent="center"
                        {...leyendaGridProps}
                        id={`${idCompPrefix}-grid-footer-leyenda`}
                        container
                        item
                     >
                        <Collapse
                           in={leyendaItems && leyendaItems.length > 0 && mostrarLeyenda}
                           sx={{width: '100%'}}
                        >
                           <GroupBox label="Leyenda" classNameLabel={classes.leyendaLabel}>
                              <LeyendaItemList
                                 items={leyendaItems || []}
                                 value={leyendaValues}
                                 tooltips={leyendaTooltips}
                                 item
                                 sx={{width: '100%'}}
                                 xs={12}
                              />
                           </GroupBox>
                        </Collapse>
                     </Grid>
                     <Grid
                        xs={5}
                        spacing={1}
                        justifyContent="flex-end"
                        alignContent="center"
                        {...pasosGridProps}
                        id={`${idCompPrefix}-grid-footer-pasos`}
                        container
                        item
                     >
                        <PtrnStepperButton
                           ref={stepperRef}
                           steps={[paso1, paso2]}
                           activeStep={pasoActivo}
                           onActiveStepChange={(nuevo) => setPasoActivo(nuevo)}
                           camposData={state}
                           ptrnRef={ptrnRef}
                           xs={12}
                           justifyContent="flex-end"
                        />
                     </Grid>
                  </Grid>
               </Grid>
            </Grid>
         </PtrnContainerSimple>
      );
      //#endregion
   }
);

export default PtrnReportePasos;
